import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppWindow,
  BarChart3,
  BotIcon,
  Brain,
  Building2,
  ChartBarIcon,
  ChevronRight,
  ClipboardList,
  Database,
  FileText,
  FolderKanban,
  GalleryVerticalEnd,
  House,
  LayoutGrid,
  MessageCircle,
  Network,
  PackageOpen,
  PanelLeftClose,
  PanelLeftOpen,
  Server,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';

import { cn } from '../../lib/utils';
import { applyOrder } from '../../lib/profileUi';
import { useIsOrganizationAdmin, useIsSuperAdmin } from '../../stores/authStore';
import { useLayoutStore, useSidebarCollapsed } from '../../stores/layoutStore';
import { useTourStore } from '../../stores/tourStore';
import { useOrgForge } from '../../hooks/useOrgForge';
import { useForgeApiEnabled } from '../../stores/forgeApiStore';
import { usePermissions } from '../../hooks/usePermissions';
import { isOrgModule, useOrgModules } from '../../hooks/useOrgModules';
import { useProfileUi } from '../../hooks/useProfileUi';
import type { ControlledResource, NavItemId, NavSection } from '../../types/profiles';
import { useDemoMode } from '../../hooks/useDemoMode';
import { isPathAllowedInTour } from '../../config/onboardingTours';
import { useTranslation } from 'react-i18next';
import { SidebarItemRow } from './SidebarItemRow';
import { SidebarUserCard } from './SidebarUserCard';

/** Management links follow role rules, not the profile's ui_config, so they stay out of the catalog. */
type ManagementSection = 'organisation' | 'administration';
type SidebarSectionId = NavSection | ManagementSection;

interface SidebarItem {
  /** Catalog id (types/profiles NAV_SECTIONS) used by the profile's ui_config; absent for management links. */
  id?: NavItemId;
  href: string;
  labelKey: string;
  icon: React.ElementType;
  visible: boolean;
  /** Optional onboarding tour anchor (data-tour attribute). */
  dataTour?: string;
}

interface SidebarSection {
  id: SidebarSectionId;
  labelKey?: string;
  items: SidebarItem[];
  visible: boolean;
  /** Optional onboarding tour anchor on the section container (data-tour attribute). */
  dataTour?: string;
}

interface AppSidebarProps {
  className?: string;
}

const FOLDED_SECTIONS_KEY = 'sidebar-folded-sections';

function readFoldedSections(): Partial<Record<SidebarSectionId, boolean>> {
  try {
    const raw = localStorage.getItem(FOLDED_SECTIONS_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? (parsed as Partial<Record<SidebarSectionId, boolean>>) : {};
  } catch {
    return {};
  }
}

function writeFoldedSections(value: Partial<Record<SidebarSectionId, boolean>>): void {
  try {
    localStorage.setItem(FOLDED_SECTIONS_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable — folding just won't be remembered */
  }
}

export const AppSidebar: React.FC<AppSidebarProps> = ({ className }) => {
  const [foldedSections, setFoldedSections] = useState(readFoldedSections);
  const toggleSection = useCallback((id: SidebarSectionId, currentlyFolded: boolean) => {
    setFoldedSections((prev) => {
      const next = { ...prev, [id]: !currentlyFolded };
      writeFoldedSections(next);
      return next;
    });
  }, []);
  const location = useLocation();
  const { t } = useTranslation();

  const { activeTourId, stepIndex } = useTourStore();

  const isTourActive = activeTourId != null;
  const isNavDisabled = (href: string) =>
    isTourActive && !isPathAllowedInTour(href, activeTourId!, stepIndex);

  const isSuperAdmin = useIsSuperAdmin();
  const isOrganizationAdmin = useIsOrganizationAdmin();
  const { hasForge } = useOrgForge();
  // Hidden while the super-admin kill-switch is off (or not yet known).
  const forgeApiEnabled = useForgeApiEnabled() === true;
  const { canRead: canReadPermission } = usePermissions();
  const { moduleEnabled } = useOrgModules();
  // Profile ui_config (plan §9.4): hidden items, per-section order, Olisé hiding.
  const { isNavItemShown, navOrder } = useProfileUi();
  // An item shows only when its org module is on AND the profile grants read.
  const canRead = (resource: ControlledResource) =>
    canReadPermission(resource) && (!isOrgModule(resource) || moduleEnabled(resource));
  const toggleSidebar = useLayoutStore((s) => s.toggleSidebar);
  const isSidebarCollapsed = useSidebarCollapsed();
  const { isDemo } = useDemoMode();

  const sidebarConfig: SidebarSection[] = [
    {
      id: 'main',
      visible: true,
      items: [
        {
          id: 'welcome',
          href: '/welcome',
          labelKey: 'sidebar.welcome',
          icon: Sparkles,
          visible: canRead('genie-artifact'),
          dataTour: 'nav-welcome',
        },
        {
          id: 'dashboard',
          href: '/dashboard',
          labelKey: 'sidebar.dashboard',
          icon: House,
          visible: canRead('domains'),
          dataTour: 'nav-dashboard',
        },
        // Top group, next to the home item (like Olisé); its catalog id stays in
        // the profile's `projects` section, so hiding it per profile still works.
        {
          id: 'gallery',
          href: '/galerie',
          labelKey: 'sidebar.gallery',
          icon: GalleryVerticalEnd,
          // The gallery lists projects: it needs the projects module too.
          visible: canRead('gallery') && moduleEnabled('projects'),
        },
      ],
    },
    {
      id: 'business',
      labelKey: 'sidebar.sections.business',
      visible: true,
      dataTour: 'sidebar-business',
      items: [
        {
          id: 'domains',
          href: '/domains',
          labelKey: 'sidebar.domains.root',
          icon: LayoutGrid,
          visible: canRead('domains'),
        },
        {
          id: 'usecases',
          href: '/cas-usage/list',
          labelKey: 'sidebar.useCases.root',
          icon: ClipboardList,
          visible: canRead('usecases'),
        },
        // Projects (lot L1): org modules off by default, so these stay hidden
        // unless the organization turns them on. Their catalog ids stay in the
        // profile's `projects` section, like the gallery.
        {
          id: 'projects',
          href: '/projets',
          labelKey: 'sidebar.projects',
          icon: FolderKanban,
          visible: canRead('projects'),
        },
        {
          id: 'portfolio',
          href: '/portefeuille',
          labelKey: 'sidebar.portfolio',
          icon: Network,
          // The portfolio maps projects: it needs the projects module too.
          visible: canRead('portfolio') && moduleEnabled('projects'),
        },
      ],
    },
    {
      id: 'deployment',
      labelKey: 'sidebar.sections.deployment',
      visible: true,
      items: [
        {
          id: 'cadrages',
          href: '/cadrages',
          labelKey: 'sidebar.cadrages.root',
          icon: FileText,
          visible: canRead('cadrages'),
        },
        {
          id: 'cowork',
          href: '/cowork',
          labelKey: 'sidebar.cowork',
          icon: PackageOpen,
          visible: canRead('cowork'),
          dataTour: 'nav-cowork',
        },
        // Catalog id stays in the profile's `knowledge` section.
        {
          id: 'conversations',
          href: '/conversations',
          labelKey: 'sidebar.knowledge.conversations',
          icon: MessageCircle,
          visible: canRead('conversations'),
          dataTour: 'nav-conversations',
        },
        {
          id: 'applications',
          href: '/applications',
          labelKey: 'sidebar.forge.applications',
          icon: AppWindow,
          visible: canRead('applications') && (isSuperAdmin || hasForge) && forgeApiEnabled && !isDemo,
        },
        {
          id: 'forges',
          href: '/forges',
          labelKey: 'sidebar.forge.forges',
          icon: Server,
          visible: canRead('forges') && (isSuperAdmin || hasForge) && forgeApiEnabled && !isDemo,
        },
      ],
    },
    {
      id: 'knowledge',
      labelKey: 'sidebar.sections.knowledge',
      visible: true,
      dataTour: 'sidebar-knowledge',
      items: [
        {
          id: 'chatbots',
          href: '/chatbots',
          labelKey: 'sidebar.knowledge.chatbots',
          icon: BotIcon,
          visible: canRead('chatbots'),
          dataTour: 'nav-chatbots',
        },
        {
          id: 'knowledge-base',
          href: '/knowledge-base',
          labelKey: 'sidebar.knowledge.knowledgeBase',
          icon: Database,
          visible: canRead('knowledge-base'),
          dataTour: 'nav-knowledge-base',
        },
        {
          id: 'evaluations',
          href: '/evaluations',
          labelKey: 'sidebar.evaluations',
          icon: ChartBarIcon,
          visible: canRead('evaluations') && !isDemo,
        },
      ],
    },
    {
      id: 'organisation',
      labelKey: 'sidebar.sections.organisation',
      visible: true,
      dataTour: 'sidebar-organisation',
      items: [
        {
          href: '/my-organization',
          labelKey: 'sidebar.manageOrganisation',
          icon: Building2,
          visible: canReadPermission('my-organization'),
        },
        {
          href: '/admin/configuration',
          labelKey: 'sidebar.manageAI',
          icon: Brain,
          visible: canReadPermission('configuration'),
        },
        {
          href: '/token-consumption',
          labelKey: 'sidebar.aiConsumption',
          icon: BarChart3,
          visible: isOrganizationAdmin,
        },
      ],
    },
    {
      id: 'administration',
      labelKey: 'sidebar.administration',
      // Profiles and other administration entries are super-admin (God) only.
      visible: isSuperAdmin,
      items: [
        {
          href: '/organizations',
          labelKey: 'sidebar.organizations.root',
          icon: Building2,
          visible: isSuperAdmin,
        },
        {
          href: '/users',
          labelKey: 'sidebar.users',
          icon: Users,
          visible: isSuperAdmin,
        },
        {
          href: '/forge-dashboard',
          labelKey: 'sidebar.forgeDashboard',
          icon: Database,
          visible: isSuperAdmin,
        },
        {
          href: '/admin/profiles',
          labelKey: 'sidebar.profiles',
          icon: ShieldCheck,
          visible: true,
        },
      ],
    },
  ];

  const isCatalogSection = (id: SidebarSectionId): id is NavSection =>
    id !== 'organisation' && id !== 'administration';

  // Apply the profile's hiding and ordering. With no ui_config (and the
  // Établi experience) this is the identity: same items, same order.
  const resolvedSections = sidebarConfig.map((section) => ({
    ...section,
    items: applyOrder(
      section.items.filter((item) => item.visible && (!item.id || isNavItemShown(item.id))),
      (item) => item.id ?? item.href,
      isCatalogSection(section.id) ? navOrder[section.id] : undefined
    ),
  }));

  const isActive = (href: string) => {
    if (href === '/dashboard' || href === '/forge-dashboard') {
      return location.pathname === href;
    }
    // Use cases item points at the list, but should stay active across the
    // whole /cas-usage tree (dashboard, detail, new).
    if (href === '/cas-usage/list') {
      return location.pathname === '/cas-usage' || location.pathname.startsWith('/cas-usage/');
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  // Navigating into a folded section unfolds it, so the active item is never
  // hidden; the user can still fold it again afterwards.
  const activeSectionId = resolvedSections.find((section) =>
    section.items.some((item) => isActive(item.href))
  )?.id;
  useEffect(() => {
    if (activeSectionId && foldedSections[activeSectionId]) {
      toggleSection(activeSectionId, true);
    }
    // Only on navigation: re-folding the active section by hand must stick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionId, location.pathname]);

  const renderSidebarItem = (item: SidebarItem) => {
    const active = isActive(item.href);
    const disabled = isNavDisabled(item.href);
    const label = t(item.labelKey);

    const linkClass = cn(
      "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full text-left",
      "hover:bg-muted/50 hover:text-foreground",
      active
        ? "bg-primary/10 text-primary border-r-2 border-primary"
        : "text-muted-foreground",
      isSidebarCollapsed && "justify-center",
      disabled && "opacity-50 pointer-events-none cursor-not-allowed"
    );

    const inner = (
      <>
        {item.icon && <item.icon className="h-4 w-4 flex-shrink-0" />}
        {!isSidebarCollapsed && <span>{label}</span>}
      </>
    );

    return (
      <SidebarItemRow
        key={item.href}
        label={label}
        showTooltip={isSidebarCollapsed}
      >
        {(rowRef) =>
          disabled ? (
            <span ref={rowRef} className={linkClass} aria-label={label} data-tour={item.dataTour}>
              {inner}
            </span>
          ) : (
            <Link
              ref={rowRef}
              to={item.href}
              className={linkClass}
              aria-label={label}
              data-tour={item.dataTour}
            >
              {inner}
            </Link>
          )
        }
      </SidebarItemRow>
    );
  };

  return (
    <div
      className={cn(
        "border-r border-border h-full flex flex-col transition-all duration-300 relative",
        "bg-gradient-to-b from-card to-card/95",
        isSidebarCollapsed ? "w-16" : "w-[15rem]",
        className
      )}
      data-tour="sidebar"
    >
      {/* Toggle Button - Positioned on right border */}
      <button
        onClick={toggleSidebar}
        className="absolute top-2 z-10 p-1.5 rounded-full bg-card border border-border shadow-md hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors" style={{ right: '-1.1rem' }}
        title={isSidebarCollapsed ? t('sidebar.expand') : t('sidebar.collapse')}
      >
        {isSidebarCollapsed ? (
          <PanelLeftOpen className="h-4 w-4" />
        ) : (
          <PanelLeftClose className="h-4 w-4" />
        )}
      </button>

      {/* Zone 1 – Main Navigation (scrollable) */}
      <div className="flex-1 p-4 space-y-4 overflow-y-auto mt-2" style={{ paddingTop: '0.5rem' }}>
        {resolvedSections.map((section, index) => {
          if (!section.visible) return null;
          const sectionItems = section.items;
          if (sectionItems.length === 0) return null;

          const hasHeader = !isSidebarCollapsed && !!section.labelKey;
          const folded = hasHeader && !!foldedSections[section.id];

          return (
            <div key={`${section.id}-${index}`} className="space-y-1" data-tour={section.dataTour}>
              {hasHeader && section.labelKey && (
                <button
                  type="button"
                  onClick={() => toggleSection(section.id, folded)}
                  aria-expanded={!folded}
                  className="flex h-4 w-full items-center gap-1.5 px-3 mb-1.5 text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                >
                  <span className="whitespace-nowrap text-[10.5px] font-semibold uppercase tracking-wide">
                    {t(section.labelKey)}
                  </span>
                  <span className="h-px flex-1 bg-border" />
                  <ChevronRight
                    className={cn('h-3 w-3 shrink-0 transition-transform', !folded && 'rotate-90')}
                  />
                </button>
              )}
              {!folded && sectionItems.map((item) => renderSidebarItem(item))}
            </div>
          );
        })}
      </div>

      {/* Zone 2 – Account (pinned below the scrollable navigation) */}
      <div className={cn('border-t border-border', isSidebarCollapsed ? 'p-2' : 'px-2 py-3')}>
        <SidebarUserCard collapsed={isSidebarCollapsed} />
      </div>
    </div>
  );
};
